import type {
  RuntimeConfiguration,
  SecretReference,
  S3Configuration,
} from "@proof/runtime-config";

export const setupDraftKey = "proof-instance-setup-public-v1";
export const draftDefaults = {
  profile: "development",
  name: "Proof Studio",
  username: "",
  publicOrigin: "",
  dataDirectory: "",
  embedOrigins: "",
  proxies: "",
  hstsDays: "365",
  postgresPlaintext: "false",
  caFile: "",
  objects: "postgres",
  artifacts: "postgres",
  objectEndpoint: "",
  objectRegion: "us-east-1",
  objectBucket: "",
  objectPrefix: "proof-sources/v1",
  objectPathStyle: "false",
  objectHttp: "false",
  artifactEndpoint: "",
  artifactRegion: "us-east-1",
  artifactBucket: "",
  artifactPrefix: "proof-artifacts/v1",
  artifactPathStyle: "false",
  artifactHttp: "false",
  identity: "development",
  identityEndpoint: "",
  identityHttp: "false",
  contextEnabled: "false",
  contextEndpoint: "",
  contextHttp: "false",
  deliveryEnabled: "false",
  deliveryEndpoint: "",
  deliveryId: "company-orders-v1",
  deliveryLabel: "Ordering system",
  deliveryTimeout: "4000",
  deliveryHttp: "false",
  sessionMinutes: "480",
  concurrency: "2",
  leaseSeconds: "60",
  queue: "embedded",
  redisCaEnabled: "false",
};
export type SetupDraft = typeof draftDefaults;
export const secretKeys = [
  "bootstrap",
  "password",
  "confirm",
  "database",
  "login",
  "rate",
  "objectAccess",
  "objectSecret",
  "objectSession",
  "artifactAccess",
  "artifactSecret",
  "artifactSession",
  "identity",
  "context",
  "delivery",
  "redis",
  "redisCa",
] as const;
export type SecretKey = (typeof secretKeys)[number];
export type SetupSecrets = Record<
  SecretKey,
  { mode: "value" | "file"; value: string }
>;
export const emptySecrets = () =>
  Object.fromEntries(
    secretKeys.map((key) => [key, { mode: "value", value: "" }]),
  ) as SetupSecrets;
export const list = (text: string) =>
  text
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);
export function readSetupDraft(storage?: Pick<Storage, "getItem">): SetupDraft {
  const result = {
    ...draftDefaults,
    publicOrigin: typeof location === "undefined" ? "" : location.origin,
  };
  try {
    const parsed = JSON.parse(storage?.getItem(setupDraftKey) || "{}");
    for (const key of Object.keys(draftDefaults) as (keyof SetupDraft)[])
      if (typeof parsed[key] === "string" && parsed[key].length <= 16384)
        result[key] = parsed[key];
  } catch {}
  return result;
}
const absolute = (text: string) =>
  /^(?:[a-z]:[\\/]|\\\\[^\\]+\\|\/)/i.test(text) &&
  !/(?:^|[\\/])\.{1,2}(?:[\\/]|$)/.test(text);
const integer = (text: string, min: number, max: number) =>
  /^\d+$/.test(text) && Number(text) >= min && Number(text) <= max;
const origin = (text: string) => {
  try {
    const url = new URL(text);
    return ["http:", "https:"].includes(url.protocol) && url.origin === text;
  } catch {
    return false;
  }
};
const endpoint = (text: string) => {
  try {
    const url = new URL(text);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
};
export function validateSetupStep(
  step: number,
  d: SetupDraft,
  s: SetupSecrets,
): string | undefined {
  const secret = (key: SecretKey, min = 1, max = 65536) =>
    s[key].mode === "file"
      ? absolute(s[key].value)
      : s[key].value.length >= min && s[key].value.length <= max;
  if (step === 0) {
    if (!["development", "production"].includes(d.profile))
      return "Choose a deployment profile.";
    if (!origin(d.publicOrigin))
      return "Enter the canonical public origin, including its scheme and optional port, with no path.";
    if (!absolute(d.dataDirectory))
      return "Enter an absolute data folder on the API host.";
    if (
      list(d.embedOrigins).length > 32 ||
      list(d.embedOrigins).some((item) => !origin(item))
    )
      return "Enter up to 32 canonical embed origins.";
    if (
      d.profile === "production" &&
      (!d.publicOrigin.startsWith("https:") ||
        list(d.embedOrigins).some((item) => !item.startsWith("https:")))
    )
      return "Production requires HTTPS public and embed origins.";
  }
  if (step === 1) {
    if (
      !d.name.trim() ||
      d.name.trim().length > 200 ||
      !/^[a-z0-9][a-z0-9._-]{2,63}$/i.test(d.username.trim())
    )
      return "Enter an instance name and an owner username of 3–64 letters, numbers, dots, underscores or hyphens.";
    if (
      s.password.value.length < 12 ||
      s.password.value.length > 128 ||
      s.password.value !== s.confirm.value
    )
      return "Use a 12–128 character owner password and enter the same confirmation.";
    if (s.bootstrap.value.length < 32 || s.bootstrap.value.length > 1024)
      return "Enter the bootstrap token supplied by the API administrator.";
    if (list(d.proxies).length > 20 || !integer(d.hstsDays, 0, 730))
      return "Use at most 20 trusted proxy IPs and an HSTS duration between 0 and 730 days.";
    if (
      d.profile === "production" &&
      (!list(d.proxies).length || Number(d.hstsDays) < 365)
    )
      return "Production requires trusted proxy IPs and at least 365 HSTS days.";
  }
  if (step === 2) {
    if (!secret("database"))
      return "Enter a PostgreSQL connection URL or an absolute mounted-secret file.";
    if (s.database.mode === "value") {
      try {
        const url = new URL(s.database.value);
        if (
          !["postgres:", "postgresql:"].includes(url.protocol) ||
          !url.username ||
          !url.password ||
          url.search ||
          url.hash ||
          url.pathname.length < 2
        )
          return "Enter a PostgreSQL URL containing username, password and database, with no query or fragment.";
      } catch {
        return "Enter a valid PostgreSQL connection URL.";
      }
    }
    if (d.caFile && (!absolute(d.caFile) || d.postgresPlaintext === "true"))
      return "A mounted CA file requires TLS and an absolute host path.";
    if (d.profile === "production" && d.postgresPlaintext === "true")
      return "Production requires PostgreSQL TLS.";
  }
  if (step === 3) {
    if (
      d.profile === "production" &&
      (d.objects !== "s3" ||
        d.artifacts !== "s3" ||
        d.objectHttp === "true" ||
        d.artifactHttp === "true")
    )
      return "Production requires private S3 source/artifact providers over TLS.";
    for (const which of ["object", "artifact"] as const) {
      if ((which === "object" ? d.objects : d.artifacts) !== "s3") continue;
      const prefix = d[`${which}Prefix`],
        url = d[`${which}Endpoint`];
      if (
        (url && !origin(url)) ||
        !d[`${which}Bucket`] ||
        !d[`${which}Region`] ||
        !/^[a-zA-Z0-9][a-zA-Z0-9/_-]{0,199}$/.test(prefix) ||
        prefix.endsWith("/")
      )
        return "Enter an S3 bucket, region, safe prefix and optional credential-free endpoint origin.";
      if (url.startsWith("http:") && d[`${which}Http`] !== "true")
        return "Explicitly allow HTTP for a local S3-compatible endpoint.";
      if (!secret(`${which}Access`) || !secret(`${which}Secret`))
        return "Enter both S3 access and secret keys, or mounted-secret paths.";
    }
  }
  if (step === 4) {
    if (!["embedded", "redis"].includes(d.queue))
      return "Choose a queue provider.";
    if (
      d.profile === "production" &&
      (d.queue !== "redis" ||
        d.identity !== "http" ||
        d.identityHttp === "true" ||
        (d.contextEnabled === "true" && d.contextHttp === "true") ||
        (d.deliveryEnabled === "true" && d.deliveryHttp === "true"))
    )
      return "Production requires Redis over TLS and HTTPS host identity/context/ordering.";
    if (d.queue === "redis") {
      if (!secret("redis", 1, 4096))
        return "Supply a Redis URL or mounted-secret file.";
      if (s.redis.mode === "value") {
        try {
          const url = new URL(s.redis.value);
          if (
            !["redis:", "rediss:"].includes(url.protocol) ||
            !url.hostname ||
            url.search ||
            url.hash ||
            (d.profile === "production" &&
              (url.protocol !== "rediss:" || !url.password))
          )
            throw new Error();
        } catch {
          return "Use a redis:// URL for development or rediss:// with TLS; production requires rediss://.";
        }
      }
      if (d.redisCaEnabled === "true" && !secret("redisCa"))
        return "Supply the Redis CA certificate or its mounted-secret file.";
    }
    if (
      !secret("login", 32, 1024) ||
      !secret("rate", 32, 4096) ||
      (s.login.mode === "value" &&
        s.rate.mode === "value" &&
        s.login.value === s.rate.value)
    )
      return "Supply two distinct keys of at least 32 characters for operator login and request limiting.";
    if (d.deliveryEnabled === "true" && d.identity !== "http")
      return "Order delivery requires an HTTP host identity adapter.";
    if (
      d.deliveryEnabled === "true" &&
      (!/^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,199}$/.test(d.deliveryId) ||
        !d.deliveryLabel.trim() ||
        d.deliveryLabel.trim().length > 100 ||
        /[\u0000-\u001f\u007f]/.test(d.deliveryLabel) ||
        !integer(d.deliveryTimeout, 100, 5000))
    )
      return "Use a stable ordering host ID, a display label up to 100 characters and a 100–5,000 ms timeout.";
    for (const enabled of ["identity", "context", "delivery"] as const) {
      if (
        enabled === "identity"
          ? d.identity !== "http"
          : d[`${enabled}Enabled`] !== "true"
      )
        continue;
      if (!endpoint(d[`${enabled}Endpoint`]) || !secret(enabled))
        return "Enter a valid host endpoint and service credential or mounted-secret path.";
      if (
        d[`${enabled}Endpoint`].startsWith("http:") &&
        d[`${enabled}Http`] !== "true"
      )
        return "Explicitly allow HTTP for a local host adapter.";
    }
    if (
      !integer(d.sessionMinutes, 1, 480) ||
      !integer(d.concurrency, 1, 32) ||
      !integer(d.leaseSeconds, 1, 300)
    )
      return "Use 1–480 session minutes, 1–32 render slots and a 1–300 second lease.";
  }
}
export function setupCandidate(d: SetupDraft, s: SetupSecrets) {
  const secrets: Record<string, string> = {};
  const ref = (key: SecretKey, name: string): SecretReference => {
    if (s[key].mode === "file") return { kind: "file", path: s[key].value };
    secrets[name] = s[key].value;
    return { kind: "managed", name };
  };
  const s3 = (which: "object" | "artifact"): S3Configuration => ({
    kind: "s3",
    ...(d[`${which}Endpoint`] ? { endpoint: d[`${which}Endpoint`] } : {}),
    bucket: d[`${which}Bucket`],
    region: d[`${which}Region`],
    prefix: d[`${which}Prefix`],
    pathStyle: d[`${which}PathStyle`] === "true",
    allowHttp: d[`${which}Http`] === "true",
    timeoutMs: 15000,
    accessKey: ref(`${which}Access`, `${which}-access`),
    secretKey: ref(`${which}Secret`, `${which}-secret`),
    ...(s[`${which}Session`].value
      ? { sessionToken: ref(`${which}Session`, `${which}-session`) }
      : {}),
  });
  const configuration: RuntimeConfiguration = {
    version: 1,
    profile: d.profile === "production" ? "production" : "development",
    name: d.name.trim(),
    dataDirectory: d.dataDirectory,
    http: {
      publicOrigin: d.publicOrigin,
      embedOrigins: list(d.embedOrigins),
      trustedProxies: list(d.proxies),
      hstsSeconds: Number(d.hstsDays) * 86400,
    },
    postgres: {
      url: ref("database", "database"),
      allowInsecure: d.postgresPlaintext === "true",
      ...(d.caFile ? { ca: { kind: "file" as const, path: d.caFile } } : {}),
      poolMax: 10,
      connectTimeoutMs: 3000,
      transactionTimeoutMs: 15000,
    },
    objects:
      d.objects === "s3"
        ? s3("object")
        : { kind: d.objects === "filesystem" ? "filesystem" : "postgres" },
    artifacts: d.artifacts === "s3" ? s3("artifact") : { kind: "postgres" },
    queue:
      d.queue === "redis"
        ? {
            kind: "redis",
            url: ref("redis", "redis-url"),
            ...(d.redisCaEnabled === "true"
              ? { ca: ref("redisCa", "redis-ca") }
              : {}),
          }
        : { kind: "embedded" },
    identity:
      d.identity === "http"
        ? {
            kind: "http",
            endpoint: d.identityEndpoint,
            token: ref("identity", "identity"),
            allowHttp: d.identityHttp === "true",
            timeoutMs: 5000,
          }
        : { kind: "development" },
    ...(d.contextEnabled === "true"
      ? {
          context: {
            endpoint: d.contextEndpoint,
            token: ref("context", "context"),
            allowHttp: d.contextHttp === "true",
            timeoutMs: 5000,
          },
        }
      : {}),
    ...(d.deliveryEnabled === "true"
      ? {
          delivery: {
            kind: "http" as const,
            id: d.deliveryId,
            label: d.deliveryLabel.trim(),
            endpoint: d.deliveryEndpoint,
            token: ref("delivery", "order-delivery"),
            allowHttp: d.deliveryHttp === "true",
            timeoutMs: Number(d.deliveryTimeout),
          },
        }
      : {}),
    security: {
      loginKey: ref("login", "operator-login"),
      rateKey: ref("rate", "request-rate"),
      sessionMs: Number(d.sessionMinutes) * 60000,
    },
    renderer: {
      concurrency: Number(d.concurrency),
      leaseMs: Number(d.leaseSeconds) * 1000,
    },
  };
  const result = {
    configuration,
    owner: {
      username: d.username.trim().toLowerCase(),
      password: s.password.value,
    },
    secrets,
  };
  if (new TextEncoder().encode(JSON.stringify(result)).length > 65536)
    throw new Error("The setup request exceeds 64 KiB.");
  return result;
}
export function safeOperatorReturn(value?: string | null) {
  return value === "/admin/renders" ||
    value === "/operator/configuration" ||
    value === "/operator/impersonation"
    ? value
    : "/operator";
}

/** Thrown when a file is not a setup file this wizard can reopen. */
export class SetupFileError extends Error {}

/**
 * Reopen a file saved with "Download setup file": the inverse of
 * setupCandidate. Values not represented in the wizard keep their defaults;
 * managed secret values come back as typed values and file references as
 * file paths. The owner password is never restored.
 */
export function draftFromSetupFile(input: unknown): {
  draft: SetupDraft;
  secrets: SetupSecrets;
  restored: string[];
} {
  const file = input as {
    configuration?: Partial<RuntimeConfiguration> & Record<string, any>;
    owner?: { username?: unknown };
    secrets?: Record<string, unknown>;
  };
  if (!file || typeof file !== "object" || !file.configuration)
    throw new SetupFileError(
      'This isn’t a Proof Studio setup file. It should have a "configuration" section.',
    );
  const c = file.configuration as Record<string, any>;
  if (c.version !== 1)
    throw new SetupFileError(
      `This setup file is version ${JSON.stringify(c.version)}. This wizard reads version 1.`,
    );
  const managed = file.secrets && typeof file.secrets === "object" ? file.secrets : {};
  const draft: SetupDraft = { ...draftDefaults };
  const secrets = emptySecrets();
  const restored: string[] = [];
  const text = (value: unknown) => (typeof value === "string" ? value : undefined);
  const bool = (value: unknown) => (value === true ? "true" : "false");
  const set = (key: keyof SetupDraft, value: unknown) => {
    const next =
      typeof value === "number" ? String(value) : typeof value === "string" ? value : undefined;
    if (next !== undefined) draft[key] = next;
  };
  const secret = (key: SecretKey, ref: unknown) => {
    const r = ref as SecretReference | undefined;
    if (!r || typeof r !== "object") return;
    if (r.kind === "file" && typeof r.path === "string")
      secrets[key] = { mode: "file", value: r.path };
    else if (r.kind === "managed" && typeof managed[r.name] === "string")
      secrets[key] = { mode: "value", value: managed[r.name] as string };
  };
  set("profile", c.profile);
  set("name", c.name);
  set("dataDirectory", c.dataDirectory);
  if (c.http) {
    set("publicOrigin", c.http.publicOrigin);
    if (Array.isArray(c.http.embedOrigins)) draft.embedOrigins = c.http.embedOrigins.join("\n");
    if (Array.isArray(c.http.trustedProxies)) draft.proxies = c.http.trustedProxies.join("\n");
    if (typeof c.http.hstsSeconds === "number")
      draft.hstsDays = String(Math.round(c.http.hstsSeconds / 86400));
    restored.push("Web address");
  }
  if (c.postgres) {
    secret("database", c.postgres.url);
    draft.postgresPlaintext = bool(c.postgres.allowInsecure);
    if (c.postgres.ca?.kind === "file") set("caFile", c.postgres.ca.path);
    restored.push("Database");
  }
  const s3 = (which: "object" | "artifact", value: any) => {
    set(`${which}Endpoint`, value.endpoint ?? "");
    set(`${which}Bucket`, value.bucket);
    set(`${which}Region`, value.region);
    set(`${which}Prefix`, value.prefix);
    draft[`${which}PathStyle`] = bool(value.pathStyle);
    draft[`${which}Http`] = bool(value.allowHttp);
    secret(`${which}Access`, value.accessKey);
    secret(`${which}Secret`, value.secretKey);
    secret(`${which}Session`, value.sessionToken);
  };
  if (c.objects?.kind) {
    draft.objects = c.objects.kind;
    if (c.objects.kind === "s3") s3("object", c.objects);
    restored.push("Source storage");
  }
  if (c.artifacts?.kind) {
    draft.artifacts = c.artifacts.kind;
    if (c.artifacts.kind === "s3") s3("artifact", c.artifacts);
    restored.push("Output storage");
  }
  if (c.queue?.kind) {
    draft.queue = c.queue.kind;
    if (c.queue.kind === "redis") {
      secret("redis", c.queue.url);
      draft.redisCaEnabled = bool(!!c.queue.ca);
      secret("redisCa", c.queue.ca);
    }
    restored.push("Job queue");
  }
  if (c.identity?.kind) {
    draft.identity = c.identity.kind;
    if (c.identity.kind === "http") {
      set("identityEndpoint", c.identity.endpoint);
      draft.identityHttp = bool(c.identity.allowHttp);
      secret("identity", c.identity.token);
    }
    restored.push("Sign-in");
  }
  if (c.context) {
    draft.contextEnabled = "true";
    set("contextEndpoint", c.context.endpoint);
    draft.contextHttp = bool(c.context.allowHttp);
    secret("context", c.context.token);
  }
  if (c.delivery) {
    draft.deliveryEnabled = "true";
    set("deliveryId", c.delivery.id);
    set("deliveryLabel", c.delivery.label);
    set("deliveryEndpoint", c.delivery.endpoint);
    set("deliveryTimeout", c.delivery.timeoutMs);
    draft.deliveryHttp = bool(c.delivery.allowHttp);
    secret("delivery", c.delivery.token);
    restored.push("Order delivery");
  }
  if (c.security) {
    secret("login", c.security.loginKey);
    secret("rate", c.security.rateKey);
    if (typeof c.security.sessionMs === "number")
      draft.sessionMinutes = String(Math.round(c.security.sessionMs / 60000));
    restored.push("Security keys");
  }
  if (c.renderer) {
    set("concurrency", c.renderer.concurrency);
    if (typeof c.renderer.leaseMs === "number")
      draft.leaseSeconds = String(Math.round(c.renderer.leaseMs / 1000));
  }
  const username = text(file.owner?.username);
  if (username) draft.username = username;
  return { draft, secrets, restored };
}
